import { useCallback, useEffect, useSyncExternalStore } from 'react'
import {
  CELL_CHARGE,
  CELL_POINTS,
  HEAT,
  LIVES,
  TIME_LAPSE,
  gaitAllowed,
  type DashState,
  type GaitKind,
} from '@/domain/hs1'
import { APFEL, ITEM_COLORS } from './palette'
import type { DashStore } from './store'
import './dash.css'

/**
 * Die Anzeige des Spiels.
 *
 * SIE LIEST DEN ZUSTAND UND SCHREIBT IHN NIE. Jede Zahl hier steht so im Kern;
 * die einzige Rechnung in dieser Datei ist die Umrechnung eines Prozentwerts in
 * eine Balkenbreite.
 *
 * WAS SIE BEWUSST ZEIGT: die Grössen, die aus der Maschine kommen — Hüftmoment
 * gegen die Grenze aus den Winkelfreiheiten, Antriebsleistung, Akkuinhalt. Das
 * ist der Witz des ganzen Easter Eggs. Ein Läufer mit erfundenen Balken wäre ein
 * Läufer; dieser hier verliert, weil ein Akku leer wird, den die Stückliste
 * ausgelegt hat.
 */

/**
 * Hüftmoment je Gangart, Nm — aus dem Antriebsmodell.
 *
 * Als Tabelle und nicht live gerechnet: der Wert hängt am Gangbild und nicht am
 * Spielverlauf, und ein zweiter Durchlauf je Bild wäre für eine Anzeige, die
 * sich nur beim Gangartwechsel ändert, verschwendet.
 */
const HIP_MOMENT: Partial<Record<GaitKind, number>> = {
  walk: 152,
  run: 268,
  sprint: 387,
  stand: 40,
}

/** Die Grenze des Nickmoduls aus den Winkelfreiheiten, Nm. */
const HIP_LIMIT = 212

const GAITS: { key: GaitKind; label: string; note: string; hint: string }[] = [
  { key: 'walk', label: 'Gehen', note: '1,1 m/s', hint: 'kühlt die Hüfte' },
  { key: 'run', label: 'Laufen', note: '2,3 m/s', hint: 'Standardtempo' },
  { key: 'sprint', label: 'Sprint', note: '4,0 m/s', hint: 'heizt schnell' },
]

export interface DashHudProps {
  store: DashStore
  gait: GaitKind
  /** Mittlere Antriebsleistung des laufenden Gangbilds, W. */
  power: number
  /** Akkuinhalt, J. */
  capacity: number
  legend: boolean
  onLegend: (open: boolean) => void
  onGait: (gait: GaitKind) => void
  onJump: () => void
  onRestart: () => void
  onQuit: () => void
  /** Aufstehen und an der Startlinie neu beginnen. */
  onRetry: () => void
  /** Loslaufen. */
  onGo: () => void
}

export function DashHud({
  store,
  gait,
  power,
  capacity,
  legend,
  onLegend,
  onGait,
  onJump,
  onRestart,
  onQuit,
  onRetry,
  onGo,
}: DashHudProps) {
  const state = useSyncExternalStore(store.subscribe, store.get)

  if (!state.running) return null

  return (
    <div className="dash" role="region" aria-label="Gen5 Dash">
      <Gauges state={state} gait={gait} power={power} capacity={capacity} />
      <Scores state={state} />

      <button
        type="button"
        className="dash__info"
        onClick={() => onLegend(!legend)}
        aria-label="Legende"
        title="Legende · hält das Spiel an"
      >
        i
      </button>

      {state.message && (
        <p className="dash__msg" role="status">
          {state.message}
        </p>
      )}

      <p className="dash__hint">
        Hürde überspringen · Öl nur im Gehen · Riegel nur mit Sprint · Zelle im Sprung ={' '}
        {CELL_POINTS} Punkte · <b>i</b> für die Legende
      </p>

      <Keys state={state} gait={gait} onGait={onGait} onJump={onJump} onQuit={onQuit} />

      {legend && <Legend onClose={() => onLegend(false)} />}

      {/*
        DREI FENSTER MIT VERSCHIEDENEN FRAGEN, und sie schliessen sich aus.
        „Aus" beendet das Spiel, „Gestürzt" beendet nur den Versuch, und an der
        Startlinie steht gar keine Frage — nur der Hinweis, dass es losgeht,
        wenn man will.
      */}
      {state.over && <GameOver state={state} onRestart={onRestart} onQuit={onQuit} />}
      {!state.over && state.phase === 'prompt' && (
        <Fallen state={state} onRetry={onRetry} onQuit={onQuit} />
      )}
      {!state.over && state.phase === 'ready' && <AtStart onGo={onGo} />}
    </div>
  )
}

function Gauges({
  state,
  gait,
  power,
  capacity,
}: {
  state: DashState
  gait: GaitKind
  power: number
  capacity: number
}) {
  const moment = HIP_MOMENT[gait] ?? 0
  return (
    <div className={['dash__gauges', state.hot ? 'dash__gauges--hot' : ''].filter(Boolean).join(' ')}>
      <div className="dash__row">
        <span>Hüfte</span>
        <span className="numeric">{state.heat.toFixed(0)} %</span>
      </div>
      <div className="dash__bar">
        <i className="dash__fill dash__fill--heat" style={{ width: `${state.heat}%` }} />
      </div>
      {/* Echte Zahlen aus dem Modell — deshalb steht die Grenze daneben und
          nicht nur der Wert. */}
      <small className="numeric">
        {moment} / {HIP_LIMIT} Nm {moment > HIP_LIMIT && <span aria-hidden="true">⚠</span>}
      </small>

      <div className="dash__sep" />

      <div className="dash__row">
        <span>Akku</span>
        <span className="numeric">{state.battery.toFixed(0)} %</span>
      </div>
      <div className="dash__bar">
        <i
          className={['dash__fill', 'dash__fill--bat', state.battery < 25 ? 'is-low' : '']
            .filter(Boolean)
            .join(' ')}
          style={{ width: `${state.battery}%` }}
        />
      </div>
      <small className="numeric">
        {power.toFixed(0)} W · {(capacity / 3600000).toFixed(2)} kWh Pack
      </small>
    </div>
  )
}

function Scores({ state }: { state: DashState }) {
  return (
    <div className="dash__top">
      <div className="dash__stat">
        <div className="dash__lives" aria-label={`${state.lives} von ${LIVES} Hüllen`}>
          {Array.from({ length: LIVES }, (_, i) => (
            <i key={i} className={i >= state.lives ? 'is-out' : undefined} />
          ))}
        </div>
        <span>Hülle</span>
      </div>
      <div className="dash__stat">
        <b className="numeric">{Math.round(state.dist)}</b>
        <span>Meter</span>
      </div>
      <div className="dash__stat">
        <b className="numeric">{state.points}</b>
        <span>Punkte</span>
      </div>
      <div className="dash__stat">
        <b className="numeric">{state.best}</b>
        <span>Bestwert</span>
      </div>
    </div>
  )
}

function Keys({
  state,
  gait,
  onGait,
  onJump,
  onQuit,
}: {
  state: DashState
  gait: GaitKind
  onGait: (g: GaitKind) => void
  onJump: () => void
  onQuit: () => void
}) {
  return (
    <div className="dash__keys">
      {GAITS.map((g, i) => (
        <button
          key={g.key}
          type="button"
          className={gait === g.key ? 'is-on' : undefined}
          /* Die Sperre kommt aus dem Kern und nicht aus dieser Datei: ein Knopf,
             der etwas anbietet, das die Rechnung ablehnt, ist eine Lüge. */
          disabled={!gaitAllowed(state, g.key)}
          onClick={() => onGait(g.key)}
        >
          {g.label}
          <kbd>{i + 1}</kbd>
        </button>
      ))}
      <button type="button" onClick={onJump}>
        Sprung
        <kbd>Leer</kbd>
      </button>
      <button type="button" onClick={onQuit}>
        Beenden
        <kbd>Esc</kbd>
      </button>
    </div>
  )
}

function Legend({ onClose }: { onClose: () => void }) {
  return (
    <div className="dash__leg" onClick={onClose}>
      <div className="dash__legcard" onClick={(e) => e.stopPropagation()}>
        <h3>Gen5 Dash</h3>
        <p className="dash__sub">
          Seitenspielerei. Der Roboter läuft mit den vorhandenen Gangbildern, der Boden rollt mit
          der berechneten Geschwindigkeit.
        </p>

        <h4>Steuerung</h4>
        <table>
          <tbody>
            {GAITS.map((g, i) => (
              <tr key={g.key}>
                <td>
                  <kbd>{i + 1}</kbd>
                </td>
                <td>
                  {g.label} · {g.note} — {g.hint}
                </td>
              </tr>
            ))}
            <tr>
              <td>
                <kbd>Leertaste</kbd>
              </td>
              <td>
                Sprung, auch <kbd>↑</kbd>
              </td>
            </tr>
            <tr>
              <td>
                <kbd>i</kbd>
              </td>
              <td>Diese Legende, hält das Spiel an</td>
            </tr>
            <tr>
              <td>
                <kbd>Esc</kbd>
              </td>
              <td>Beenden, alles wird zurückgesetzt</td>
            </tr>
          </tbody>
        </table>

        <h4>Hindernisse</h4>
        <table>
          <tbody>
            <tr>
              <td>
                <span className="dash__dot" style={{ background: hex(ITEM_COLORS.hurdle) }} />
                Hürde
              </td>
              <td>Überspringen. Aus jeder Gangart möglich.</td>
            </tr>
            <tr>
              <td>
                <span className="dash__dot" style={{ background: hex(ITEM_COLORS.oil) }} />
                Ölfleck
              </td>
              <td>Vorher auf Gehen zurück, sonst rutscht er aus. Springen hilft auch.</td>
            </tr>
            <tr>
              <td>
                <span className="dash__dot" style={{ background: hex(ITEM_COLORS.pit) }} />
                Riegel
              </td>
              <td>1,6 m breit — nur mit Sprint zu überfliegen. Im Laufen reicht die Flugweite nicht.</td>
            </tr>
            <tr>
              <td>
                <span className="dash__dot" style={{ background: hex(APFEL) }} />
                Zelle
              </td>
              <td>Auf Kopfhöhe, im Sprung mitnehmen. {CELL_POINTS} Punkte.</td>
            </tr>
          </tbody>
        </table>

        <h4>Hitze</h4>
        <table>
          <tbody>
            <tr>
              <td>Sprint</td>
              <td>+{HEAT.sprint} % je Sekunde</td>
            </tr>
            <tr>
              <td>Laufen</td>
              <td>+{String(HEAT.run).replace('.', ',')} % je Sekunde</td>
            </tr>
            <tr>
              <td>Gehen</td>
              <td>−{Math.abs(HEAT.walk)} % je Sekunde</td>
            </tr>
            <tr>
              <td>bei {HEAT.limit} %</td>
              <td>Zwangsgang Gehen, bis die Hüfte unter {HEAT.release} % ist</td>
            </tr>
            <tr>
              <td>Nm-Anzeige</td>
              <td>
                Hüftmoment der Gangart gegen die Grenze aus den Winkelfreiheiten, {HIP_LIMIT} Nm.
                Echte Zahlen aus dem Modell.
              </td>
            </tr>
          </tbody>
        </table>

        <h4>Akku</h4>
        <table>
          <tbody>
            <tr>
              <td>Inhalt</td>
              <td>
                Wie in der Stückliste ausgelegt: mittlere Antriebsleistung beim Gehen mal drei
                Stunden
              </td>
            </tr>
            <tr>
              <td>Verbrauch</td>
              <td>
                Die echte mittlere Antriebsleistung des laufenden Gangbilds, im {TIME_LAPSE}-fachen
                Zeitraffer
              </td>
            </tr>
            <tr>
              <td>Zelle</td>
              <td>Lädt {CELL_CHARGE} % nach</td>
            </tr>
            <tr>
              <td>bei 0 %</td>
              <td>Aus, unabhängig von den Hüllen</td>
            </tr>
          </tbody>
        </table>

        <h4>Punkte</h4>
        <table>
          <tbody>
            <tr>
              <td>Meter</td>
              <td>Ein Punkt je gelaufenem Meter</td>
            </tr>
            <tr>
              <td>Zelle</td>
              <td>{CELL_POINTS} Punkte</td>
            </tr>
            <tr>
              <td>Hülle</td>
              <td>{LIVES} Treffer, dann ist Schluss</td>
            </tr>
          </tbody>
        </table>

        <button type="button" className="dash__go" onClick={onClose}>
          Weiter
        </button>
      </div>
    </div>
  )
}

/**
 * Nach dem Sturz.
 *
 * Es beendet den VERSUCH und nicht das Spiel — deshalb steht hier, was noch da
 * ist (Hüllen, Akku), und nicht nur, was weg ist. Ein Fenster, das aussieht wie
 * das Spielende, liesse einen aufhören, obwohl man noch zwei Versuche hat.
 */
function Fallen({
  state,
  onRetry,
  onQuit,
}: {
  state: DashState
  onRetry: () => void
  onQuit: () => void
}) {
  /* Enter steht auf „Neu starten" — die Hand liegt ohnehin auf der Tastatur. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return
      e.preventDefault()
      e.stopPropagation()
      onRetry()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onRetry])

  return (
    <div className="dash__over">
      <div className="dash__overcard">
        <h3>Gestürzt</h3>
        <p>{state.message || state.reason || 'Hindernis gerissen'}</p>
        <div className="dash__result">
          <div>
            <b className="numeric">{state.lives}</b>
            <span>{state.lives === 1 ? 'Hülle übrig' : 'Hüllen übrig'}</span>
          </div>
          <div>
            <b className="numeric">{state.battery.toFixed(0)}</b>
            <span>% Akku</span>
          </div>
          <div>
            <b className="numeric">{state.best}</b>
            <span>Bestwert</span>
          </div>
        </div>
        <p className="dash__sub">
          Der nächste Versuch beginnt an der Startlinie. Der Akku wird dabei nicht geladen — ein
          Sturz lädt kein Pack.
        </p>
        <button type="button" className="dash__go" onClick={onRetry}>
          Neu starten
        </button>
        <button type="button" className="dash__ghost" onClick={onQuit}>
          Beenden
        </button>
      </div>
    </div>
  )
}

/** An der Startlinie. Kein Fenster, nur ein Hinweis — er hält nichts auf. */
function AtStart({ onGo }: { onGo: () => void }) {
  return (
    <button type="button" className="dash__start" onClick={onGo}>
      Loslaufen
      <small>Leertaste oder 1 · 2 · 3</small>
    </button>
  )
}

function GameOver({
  state,
  onRestart,
  onQuit,
}: {
  state: DashState
  onRestart: () => void
  onQuit: () => void
}) {
  /* Enter für „nochmal" — die Hand liegt ohnehin auf der Tastatur. */
  const restart = useCallback(() => onRestart(), [onRestart])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        restart()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [restart])

  return (
    <div className="dash__over">
      <div className="dash__overcard">
        <h3>Aus</h3>
        <p>{state.reason}</p>
        <div className="dash__result">
          <div>
            <b className="numeric">{Math.round(state.dist)}</b>
            <span>Meter</span>
          </div>
          <div>
            <b className="numeric">{state.points}</b>
            <span>Punkte</span>
          </div>
          <div>
            <b className="numeric">{state.best}</b>
            <span>Bestwert</span>
          </div>
        </div>
        <button type="button" className="dash__go" onClick={onRestart}>
          Nochmal
        </button>
        <button type="button" className="dash__ghost" onClick={onQuit}>
          Beenden
        </button>
      </div>
    </div>
  )
}

/** Eine Materialfarbe für einen Legendenpunkt. Nur hier, nur zum Zeigen. */
const hex = (value: number) => `#${value.toString(16).padStart(6, '0')}`
